<!-- 
*****************************************************************************
**                                                                         **
** Copyright (c) 2012-2024, Tyker Technology BV                            **
** All rights reserved.                                                    **
**                                                                         **
** This software is the confidential and proprietary information of        **
** Tyker Technology BV. Unauthorized copying or disclosure of this file,   **
** via any medium is strictly prohibited. This file is only available to   **
** authorized individuals with the permission of the copyright holders.    **
** If you encounter this file and do not have permission, please contact   ** 
** the copyright holders and delete this file.                             **
**                                                                         **
***************************************************************************** 
-->

<template>
    <v-dialog
        v-model="dialog"
        persistent
        id="accountDetails"
        width="1000px"
        :fullscreen="$vuetify.breakpoint.xsOnly"
    >
        <component
            v-bind:is="currentViewComponent"
            v-on:close="closeComponent"
        ></component>
        <v-form
            ref="customerForm"
            v-model="valid"
            lazy-validation
            v-on:submit.prevent="updateData"
        >
            <v-card :loading="loading || saving">
                <template slot="progress">
                    <v-progress-linear
                        height="10"
                        indeterminate
                    ></v-progress-linear>
                </template>

                <v-card-title>
                    <div class="text-h6 text-sm-h4">
                        {{ language.t('AccountDetails.companyDetails') }}
                    </div>
                    <v-spacer></v-spacer>
                    <v-btn
                        icon
                        color="blue darken-1"
                        @click="$emit('close');"
                    >
                        <v-icon>mdi-close</v-icon>
                    </v-btn>

                    <!-- <v-btn color="success" class="ma-2" type="submit" :loading="saving"
                        :block="$vuetify.breakpoint.xsOnly" :disabled="!customerFormCompleted || !userFormCompleted">
                        {{ language.t('App.save') }}
                    </v-btn> -->
                </v-card-title>
                <v-card-text>
                    <v-container class="pa-2">
                        <v-row>
                            <v-col
                                :md="12"
                                :cols="12"
                            >
                                <v-text-field
                                    v-model="customerForm.name"
                                    :label="'*' + language.t('Forms.companyName')"
                                    required
                                    :rules="rules"
                                    :disabled="loading"
                                >
                                </v-text-field>
                            </v-col>
                            <v-col
                                :md="8"
                                :cols="12"
                            >
                                <v-text-field
                                    v-model="customerForm.street"
                                    :label="'*' + language.t('Forms.street')"
                                    required
                                    :rules="rules"
                                    :disabled="loading"
                                >
                                </v-text-field>
                            </v-col>
                            <v-col
                                :md="2"
                                :cols="4"
                            >
                                <v-text-field
                                    v-model="customerForm.houseNumber"
                                    :label="'*' + language.t('Forms.houseNumber')"
                                    required
                                    :rules="rules"
                                    :disabled="loading"
                                ></v-text-field>
                            </v-col>
                            <v-col
                                :md="2"
                                :cols="8"
                            >
                                <v-text-field
                                    v-model="customerForm.houseNumberAddition"
                                    :label="language.t('Forms.houseNumberAddition')"
                                    :disabled="loading"
                                >
                                </v-text-field>
                            </v-col>
                            <v-col
                                :md="2"
                                :cols="4"
                            >
                                <v-text-field
                                    v-model="customerForm.postalCode"
                                    :label="'*' + language.t('Forms.postalCode')"
                                    required
                                    :rules="rules"
                                    :disabled="loading"
                                >
                                </v-text-field>
                            </v-col>
                            <v-col
                                :md="10"
                                :cols="8"
                            >
                                <v-text-field
                                    v-model="customerForm.city"
                                    :label="'*' + language.t('Forms.city')"
                                    required
                                    :rules="rules"
                                    :disabled="loading"
                                >
                                </v-text-field>
                            </v-col>
                            <v-col
                                :md="6"
                                :cols="12"
                            >
                                <v-text-field
                                    v-model="customerForm.email"
                                    :label="'*' + language.t('Forms.emailAddress')"
                                    required
                                    disabled
                                >
                                </v-text-field>
                            </v-col>
                            <v-col
                                :md="6"
                                :cols="12"
                            >
                                <v-text-field
                                    v-model="customerForm.phoneNumber"
                                    :label="'*' + language.t('Forms.phoneNumber')"
                                    required
                                    :rules="rules"
                                    :disabled="loading"
                                ></v-text-field>
                            </v-col>
                            <v-col
                                :md="12"
                                :cols="12"
                            >
                                <v-text-field
                                    v-model="customerForm.website"
                                    :label="language.t('Forms.website')"
                                    :disabled="loading"
                                >
                                </v-text-field>
                            </v-col>
                        </v-row>
                    </v-container>
                </v-card-text>
            </v-card>
        </v-form>
        <v-card
            class="mt-2"
            :loading="loading || saving"
        >
            <template slot="progress">
                <v-progress-linear
                    height="10"
                    indeterminate
                ></v-progress-linear>
            </template>
            <v-card-title>
                <div class="text-h6">{{ language.t('AccountDetails.personalDetails') }}</div>
            </v-card-title>
            <v-card-text>
                <v-form
                    ref="userForm"
                    v-model="valid"
                    lazy-validation
                    v-on:submit.prevent="updateData"
                >
                    <v-container class="pa-2">
                        <v-row>
                            <v-col
                                :md="4"
                                :cols="12"
                            >
                                <v-text-field
                                    v-model="userForm.firstName"
                                    :label="language.t('Forms.firstName')"
                                    :disabled="loading"
                                >
                                </v-text-field>
                            </v-col>
                            <v-col
                                :md="4"
                                :cols="12"
                            >
                                <v-text-field
                                    v-model="userForm.middleName"
                                    :label="language.t('Forms.middleName')"
                                    :disabled="loading"
                                >
                                </v-text-field>
                            </v-col>
                            <v-col
                                :md="4"
                                :cols="12"
                            >
                                <v-text-field
                                    v-model="userForm.lastName"
                                    :label="'*' + language.t('Forms.lastName')"
                                    required
                                    :rules="rules"
                                    :disabled="loading"
                                >
                                </v-text-field>
                            </v-col>
                            <v-col
                                :md="6"
                                :cols="12"
                            >
                                <v-text-field
                                    v-model="userForm.emailAddress"
                                    :label="'*' + language.t('Forms.emailAddress')"
                                    required
                                    disabled
                                >
                                </v-text-field>
                            </v-col>
                            <v-col
                                :md="6"
                                :cols="12"
                            >
                                <v-text-field
                                    v-model="userForm.mobilePhoneNumber"
                                    :label="this.language.t('Forms.phoneNumber')"
                                    :disabled="loading"
                                >
                                </v-text-field>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col
                                :sm="6"
                                :cols="12"
                                order="1"
                                order-sm="2"
                            >
                                <p class="text-right">* = {{ language.t('Forms.requiredFields') }}</p>
                            </v-col>
                            <v-col
                                :sm="6"
                                :cols="12"
                                order="2"
                                order-sm="1"
                            >
                                <v-btn
                                    color="success"
                                    class="ma-2"
                                    type="submit"
                                    :loading="saving"
                                    :block="$vuetify.breakpoint.xsOnly"
                                    :disabled="!customerFormCompleted || !userFormCompleted"
                                >
                                    {{ language.t('App.save') }}
                                </v-btn>
                                <v-btn
                                    class="ma-2"
                                    :block="$vuetify.breakpoint.xsOnly"
                                    @click="test();"
                                >
                                    {{ language.t('AccountDetails.changePassword') }}
                                </v-btn>
                            </v-col>
                        </v-row>
                    </v-container>
                </v-form>
            </v-card-text>
            <v-card-actions class="card-actions">
                <v-spacer></v-spacer>
                <v-btn
                    color="blue darken-1"
                    text
                    @click="$emit('close');"
                >
                    <v-icon>mdi-close</v-icon>
                    {{ language.t('App.close') }}
                </v-btn>
        </v-card-actions>
    </v-card>
</v-dialog></template>

<script>
import CustomerUserDetailsPasswordEdit from './CustomerUserDetailsPasswordEdit.vue';

export default {
    components: {
        CustomerUserDetailsPasswordEdit
    },
    props: {},
    data() {
        return {
            dialog: true,
            loading: true,
            saving: false,
            valid: true,
            currentViewComponent: null,
            customerFormCompleted: false,
            userFormCompleted: false,
            customerForm: {
                name: "",
                street: "",
                houseNumber: "",
                houseNumberAddition: "",
                postalCode: "",
                city: "",
                email: "",
                phoneNumber: "",
                website: "",
            },
            userForm: {
                firstName: "",
                middleName: "",
                lastName: "",
                emailAddress: "",
                mobilePhoneNumber: "",
            },
            rules: [
                v => !!v || 'Dit veld is vereist',
            ],
        };
    },
    mounted: function () {
        this.getCustomerAccountDetails();
    },
    computed: {},
    watch: {
        customerForm: {
            /* validate if the form data is complete */
            handler(val) {
                if (val.name != "" && val.street != "" && val.houseNumber != "" && val.postalCode != "" && val.city != "" && val.phoneNumber != "")
                    this.customerFormCompleted = true;
                else
                    this.customerFormCompleted = false;
            },
            deep: true
        },
        userForm: {
            /* validate if the form data is complete */
            handler(val) {
                if (val.lastName != "")
                    this.userFormCompleted = true;
                else
                    this.userFormCompleted = false;
            },
            deep: true
        }
    },
    methods: {
        test() {
            this.currentViewComponent = CustomerUserDetailsPasswordEdit;
        },
        closeComponent() {
            this.currentViewComponent = null;
        },
        updateData() {
            this.saving = true;
            this.error = null;
            let data = new FormData();
            //required data
            data.set('name', this.customerForm.name);
            data.set('street', this.customerForm.street);
            data.set('houseNumber', this.customerForm.houseNumber);
            data.set('postalCode', this.customerForm.postalCode);
            data.set('city', this.customerForm.city);
            data.set('phoneNumber', this.customerForm.phoneNumber);
            data.set('account_lastName', this.userForm.lastName);

            //optional data
            this.customerForm.houseNumberAddition && data.set('houseNumberAddition', this.customerForm.houseNumberAddition);
            this.customerForm.website && data.set('website', this.customerForm.website);
            this.userForm.firstName && data.set('account_firstName', this.userForm.firstName);
            this.userForm.middleName && data.set('account_middleName', this.userForm.middleName);
            this.userForm.mobilePhoneNumber && data.set('account_mobilePhoneNumber', this.userForm.mobilePhoneNumber);

            this.$axios.post(route('api.customer.accountdetails.update'), data, { headers: { 'Content-Type': 'multipart/form-data' } })
                .then((res) => {
                    console.log(res.statusText);
                    this.success = true;
                    this.saving = false;
                    this.getCustomerAccountDetails();
                })
                .catch((error) => {
                    this.saving = false;
                    console.log(error.response.data);
                    if (error.response.data.error)
                        this.error = error.response.data.error;
                    else
                        this.error = "unknown server error";
                });
        },
        getCustomerAccountDetails() {
            var component = this;
            component.items = [];
            component.loading = true;

            this.$axios.get(route('api.customer.accountdetails'))
                .then((res) => {
                    var dataCustomer = res.data.dataCustomer

                    component.customerForm.name = dataCustomer.name;
                    component.customerForm.street = dataCustomer.street;
                    component.customerForm.houseNumber = dataCustomer.houseNumber;
                    component.customerForm.houseNumberAddition = dataCustomer.houseNumberAddition;
                    component.customerForm.postalCode = dataCustomer.postalCode;
                    component.customerForm.city = dataCustomer.city;
                    component.customerForm.email = dataCustomer.email;
                    component.customerForm.phoneNumber = dataCustomer.phoneNumber;
                    component.customerForm.website = dataCustomer.website;

                    var dataAccount = res.data.dataAccount

                    component.userForm.firstName = dataAccount.user.firstName;
                    component.userForm.middleName = dataAccount.user.middleName;
                    component.userForm.lastName = dataAccount.user.lastName;
                    component.userForm.emailAddress = dataAccount.user.emailAddress;
                    component.userForm.mobilePhoneNumber = dataAccount.user.mobilePhoneNumber;

                    component.loading = false;
                });
        },
        onClose() {
            this.$emit("close");
        }
    },
};
</script>
